import {
  AppBar,
  Toolbar,
  IconButton,
  Box,
  Typography,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from "@mui/material";
import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDarkMode } from "../../contexts/ThemeContext";
import { useAuth } from "../../contexts/AuthContext";
import { useRole } from "../../contexts/RoleContext";
import { useDataCache } from "../../contexts/DataCacheContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faRightFromBracket,
  faCircleUser,
  faUser,
  faGear,
  faLifeRing,
} from "@fortawesome/free-solid-svg-icons";
import LogoutModal from "../Modals/LogoutModal";
import routing from "../../routing";
import { ROLE_COLORS } from "../../constants/roles";

// helper to find page info from routing config based on current path
const getPageInfo = (pathname) => {
  // check donor routes
  for (const key of Object.keys(routing.donor)) {
    const route = routing.donor[key];
    if (route?.path && pathname === route.path) {
      return { title: route.title, subtitle: route.subtitle };
    }
  }
  // check receiver routes
  for (const key of Object.keys(routing.receiver)) {
    const route = routing.receiver[key];
    if (route?.path && pathname === route.path) {
      return { title: route.title, subtitle: route.subtitle };
    }
  }
  // check top-level shared routes
  const topLevelRoutes = ["partnerships"];
  for (const key of topLevelRoutes) {
    const route = routing[key];
    if (route?.path && pathname === route.path) {
      return { title: route.title, subtitle: route.subtitle };
    }
  }
  if (pathname === "/profile") {
    return {
      title: "Profile",
      subtitle: "Manage your account settings and preferences",
    };
  }
  return { title: null, subtitle: null };
};

function Header() {
  const { darkMode } = useDarkMode();
  const { logout } = useAuth();
  const { currentRole } = useRole();
  const { clearAllCache } = useDataCache();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const menuOpen = Boolean(anchorEl);

  const currentColors = ROLE_COLORS[currentRole];
  const { title, subtitle } = getPageInfo(location.pathname);

  // dropdown menu handlers
  const handleMenuOpen = (e) => setAnchorEl(e.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const handleNavigate = (path) => {
    handleMenuClose();
    navigate(path);
  };

  const handleLogoutClick = () => {
    handleMenuClose();
    setShowLogoutModal(true);
  };

  const handleConfirmLogout = () => {
    setShowLogoutModal(false);
    clearAllCache();
    logout();
    navigate(routing.login.path);
  };

  const handleCancelLogout = () => {
    setShowLogoutModal(false);
  };

  // shared menu item styles
  const menuItemSx = {
    fontFamily: "Poppins, sans-serif",
    fontSize: "0.875rem",
    py: 1.2,
    px: 2.5,
    gap: 1,
    transition: "background-color 0.15s ease",
    "&:hover": {
      backgroundColor: darkMode
        ? "rgba(255, 255, 255, 0.06)"
        : currentColors.hoverLight,
    },
  };

  const iconStyle = {
    fontSize: "15px",
    width: "18px",
    color: darkMode ? "#aaa" : "#666",
  };

  return (
    <>
      <AppBar
        position="static"
        elevation={0}
        sx={{
          background: darkMode
            ? "linear-gradient(90deg, #2a2a2a 0%, #252525 100%)"
            : "linear-gradient(90deg, #ffffff 0%, #fafafa 100%)",
          borderBottom: darkMode ? "1px solid #3a3a3a" : "1px solid #e0e0e0",
          color: darkMode ? "#e0e0e0" : "#000000",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* subtle accent line at bottom */}
        <Box
          sx={{
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            height: "2px",
            background: `linear-gradient(90deg, transparent, ${currentColors.primary}, transparent)`,
            opacity: 0.3,
          }}
        />

        <Toolbar
          sx={{
            justifyContent: "space-between",
            height: "72px",
            minHeight: "72px !important",
            position: "relative",
            zIndex: 1,
          }}
        >
          {/* left section - page title */}
          {title && (
            <Box
              key={location.pathname}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                opacity: 0,
                animation: "fadeInLeft 0.6s ease-out 0.1s forwards",
                "@keyframes fadeInLeft": {
                  from: { opacity: 0, transform: "translateX(-20px)" },
                  to: { opacity: 1, transform: "translateX(0)" },
                },
              }}
            >
              <Typography
                sx={{
                  fontFamily: "Poppins, sans-serif",
                  fontSize: "1.25rem",
                  fontWeight: 700,
                  color: darkMode ? "#ffffff" : "#1a1a1a",
                  letterSpacing: "-0.02em",
                }}
              >
                {t(title)}
              </Typography>
              {subtitle && (
                <>
                  <Box
                    sx={{
                      width: "1px",
                      height: "24px",
                      backgroundColor: darkMode ? "#444" : "#ccc",
                    }}
                  />
                  <Typography
                    sx={{
                      fontFamily: "Poppins, sans-serif",
                      fontSize: "0.875rem",
                      fontWeight: 400,
                      color: darkMode ? "#888" : "#666",
                    }}
                  >
                    {t(subtitle)}
                  </Typography>
                </>
              )}
            </Box>
          )}

          {/* spacer when no title */}
          {!title && <Box />}

          {/* right section - profile dropdown */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              opacity: 0,
              animation: "fadeInRight 0.6s ease-out 0.2s forwards",
              "@keyframes fadeInRight": {
                from: { opacity: 0, transform: "translateX(20px)" },
                to: { opacity: 1, transform: "translateX(0)" },
              },
            }}
          >
            <IconButton
              onClick={handleMenuOpen}
              aria-controls={menuOpen ? "profile-menu" : undefined}
              aria-haspopup="true"
              aria-expanded={menuOpen ? "true" : undefined}
              sx={{
                width: 40,
                height: 40,
                borderRadius: "10px",
                background: menuOpen
                  ? `linear-gradient(135deg, ${currentColors.primary}ee 0%, ${currentColors.primary} 100%)`
                  : `linear-gradient(135deg, ${currentColors.primary} 0%, ${currentColors.primary}dd 100%)`,
                color: "white",
                fontSize: "1.1rem",
                boxShadow: menuOpen
                  ? `0 6px 16px ${currentColors.primary}50`
                  : `0 4px 12px ${currentColors.primary}40`,
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                transform: menuOpen ? "translateY(-2px)" : "none",
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow: `0 6px 16px ${currentColors.primary}50`,
                  background: `linear-gradient(135deg, ${currentColors.primary}ee 0%, ${currentColors.primary} 100%)`,
                },
                "&:active": {
                  transform: "translateY(0)",
                },
              }}
            >
              <FontAwesomeIcon icon={faCircleUser} />
            </IconButton>

            {/* dropdown menu */}
            <Menu
              id="profile-menu"
              anchorEl={anchorEl}
              open={menuOpen}
              onClose={handleMenuClose}
              transformOrigin={{ horizontal: "right", vertical: "top" }}
              anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
              slotProps={{
                paper: {
                  elevation: 8,
                  sx: {
                    mt: 1.5,
                    minWidth: 200,
                    borderRadius: "12px",
                    backgroundColor: darkMode ? "#2a2a2a" : "#ffffff",
                    border: darkMode
                      ? "1px solid #3a3a3a"
                      : "1px solid #e8e8e8",
                    boxShadow: darkMode
                      ? "0 8px 32px rgba(0, 0, 0, 0.4)"
                      : "0 8px 32px rgba(0, 0, 0, 0.12)",
                    overflow: "hidden",
                    "& .MuiList-root": { py: 0.5 },
                  },
                },
              }}
            >
              <MenuItem
                onClick={() => handleNavigate("/profile")}
                sx={menuItemSx}
              >
                <ListItemIcon sx={{ minWidth: "unset" }}>
                  <FontAwesomeIcon icon={faUser} style={iconStyle} />
                </ListItemIcon>
                <ListItemText
                  primary={t("Profile")}
                  primaryTypographyProps={{
                    fontFamily: "Poppins, sans-serif",
                    fontSize: "0.875rem",
                    fontWeight: 500,
                    color: darkMode ? "#e0e0e0" : "#333",
                  }}
                />
              </MenuItem>

              <MenuItem
                onClick={() => handleNavigate("/profile?tab=appearance")}
                sx={menuItemSx}
              >
                <ListItemIcon sx={{ minWidth: "unset" }}>
                  <FontAwesomeIcon icon={faGear} style={iconStyle} />
                </ListItemIcon>
                <ListItemText
                  primary={t("Settings")}
                  primaryTypographyProps={{
                    fontFamily: "Poppins, sans-serif",
                    fontSize: "0.875rem",
                    fontWeight: 500,
                    color: darkMode ? "#e0e0e0" : "#333",
                  }}
                />
              </MenuItem>

              <MenuItem
                onClick={() => handleNavigate("/profile?tab=support")}
                sx={menuItemSx}
              >
                <ListItemIcon sx={{ minWidth: "unset" }}>
                  <FontAwesomeIcon icon={faLifeRing} style={iconStyle} />
                </ListItemIcon>
                <ListItemText
                  primary={t("Support")}
                  primaryTypographyProps={{
                    fontFamily: "Poppins, sans-serif",
                    fontSize: "0.875rem",
                    fontWeight: 500,
                    color: darkMode ? "#e0e0e0" : "#333",
                  }}
                />
              </MenuItem>

              <Divider
                sx={{ my: 0.5, borderColor: darkMode ? "#3a3a3a" : "#eee" }}
              />

              <MenuItem onClick={handleLogoutClick} sx={menuItemSx}>
                <ListItemIcon sx={{ minWidth: "unset" }}>
                  <FontAwesomeIcon
                    icon={faRightFromBracket}
                    style={{ ...iconStyle, color: "#e74c3c" }}
                  />
                </ListItemIcon>
                <ListItemText
                  primary={t("Logout")}
                  primaryTypographyProps={{
                    fontFamily: "Poppins, sans-serif",
                    fontSize: "0.875rem",
                    fontWeight: 500,
                    color: "#e74c3c",
                  }}
                />
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>

      {/* logout confirmation modal */}
      <LogoutModal
        open={showLogoutModal}
        onClose={handleCancelLogout}
        onConfirm={handleConfirmLogout}
        darkMode={darkMode}
      />
    </>
  );
}

export default Header;
